import type { LinkedDocumentationTarget } from 'shared/domain/issue/linkedDocumentation';
import { DisplayedFile } from 'components/common/gallery/filesStore';
import type { LinkedDocumentationItem } from 'shared/domain/issue/linkedDocumentation';
import type {
  ExportedVersionOptions,
  ExportToDocumentationResultData,
} from 'shared/domain/issue/linkedDocumentation/operations';
import type { ActionsState } from 'store/types';

export type IssueWithLinkedDocumentation = {
  linkedDocumentation?: LinkedDocumentationItem[];
};

/** An existing documentation that takes the file as its new version, and what the user chose about it (PT-7318). */
export interface ExportAsVersion {
  targetDocumentationId: string;
  version: ExportedVersionOptions;
}

/** Without `ExportAsVersion` the file is exported as a new documentation. */
export interface ExportToDocumentationArgs extends Partial<ExportAsVersion> {
  issueId: string;
  fileToExport: DisplayedFile;
  currentDirectoryId: string;
  selectedDirectoryId: string | null;
}

export interface ImportFromDocumentationArgs {
  issueId: string;
  issue: IssueWithLinkedDocumentation | undefined;
  /** The documentation, or one of its versions, to link (PT-7117). */
  target: LinkedDocumentationTarget;
}

export interface RemoveLinkedDocumentationArgs {
  issueId: string;
  linkedDocumentationId: string;
}

export type { ExportToDocumentationResultData };

export const issueDocumentationOperationNames = [
  'exportToDocumentation',
  'importFromDocumentation',
  'removeLinkedDocumentation',
] as const;

export type IssueDocumentationOperationName =
  (typeof issueDocumentationOperationNames)[number];

export type IssueDocumentationOperationsSlice = {
  _actionStates: ActionsState<IssueDocumentationOperationName>;
  lastError: string | null;
  setLastError: (message: string | null) => void;
  exportToDocumentation: (
    args: ExportToDocumentationArgs
  ) => Promise<ExportToDocumentationResultData>;
  importFromDocumentation: (args: ImportFromDocumentationArgs) => Promise<void>;
  removeLinkedDocumentation: (args: RemoveLinkedDocumentationArgs) => Promise<void>;
  resetIssueDocumentationOperations: () => void;
};
